import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const steps = [
  {
    id: "01",
    title: "Discover",
    desc: "We map your business, users and constraints. Out: a sharp brief, scope, and a no-bullshit feasibility call.",
    duration: "Week 1",
  },
  {
    id: "02",
    title: "Design",
    desc: "Architecture, design system, motion language, first interactive prototype. We design in the browser, not in slides.",
    duration: "Week 2–3",
  },
  {
    id: "03",
    title: "Build",
    desc: "Weekly drops. You can use the product the day we start. Code reviews, CI, accessibility, performance budgets.",
    duration: "Week 3–10",
  },
  {
    id: "04",
    title: "Launch",
    desc: "Migration, load tests, monitoring, on-call. Then we stay: a retainer that compounds over time.",
    duration: "Week 10 →",
  },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 30%"],
  });
  const lineScaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const lineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="process"
      data-testid="process-section"
      ref={ref}
      className="relative bg-[#070707] py-32 md:py-48 border-t border-white/[0.06]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-20 md:mb-28">
          <div className="md:col-span-4">
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/50">
              (04) — Operating System
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display font-extrabold uppercase leading-[0.9] tracking-tighter text-4xl md:text-6xl lg:text-7xl">
              A studio that<br />
              <span className="text-[#29ABE2]/80">behaves like</span><br />
              a product team.
            </h2>
          </div>
        </div>

        {/* Connector — animated on scroll */}
        <div className="relative">
          {/* Desktop horizontal line above cards */}
          <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-white/[0.06] pointer-events-none">
            <motion.div
              style={{ scaleX: lineScaleX, transformOrigin: "left" }}
              className="absolute inset-0 bg-[#29ABE2]"
            />
            {/* Step dots */}
            {steps.map((_, i) => (
              <div
                key={i}
                className="absolute -top-1.5 w-3 h-3 rounded-full bg-[#0a0a0a] border border-[#29ABE2]"
                style={{ left: `calc(${(i / (steps.length - 1)) * 100}% - 6px)` }}
              />
            ))}
          </div>

          {/* Mobile vertical line on the left */}
          <div className="md:hidden absolute left-3 top-2 bottom-2 w-px bg-white/[0.08] pointer-events-none">
            <motion.div
              style={{ scaleY: lineScaleY, transformOrigin: "top" }}
              className="absolute inset-0 bg-[#29ABE2]"
            />
          </div>

          <div className="grid md:grid-cols-4 gap-px md:bg-white/[0.06] md:border md:border-white/[0.06] pt-10 md:pt-16">
            {steps.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="bg-[#070707] p-8 md:p-10 relative group md:border-0 border-l border-white/[0.06] ml-6 md:ml-0 pl-6 md:pl-10"
                data-testid={`process-step-${s.id}`}
              >
                <div className="flex items-center justify-between mb-12">
                  <span className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/40">
                    Step {s.id}
                  </span>
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/40 border border-white/[0.08] rounded-full px-2.5 py-1">
                    {s.duration}
                  </span>
                </div>

                <h3 className="font-display font-bold text-3xl md:text-4xl mb-4 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.desc}</p>

                <div className="absolute top-0 left-0 h-px bg-[#29ABE2] w-0 group-hover:w-full transition-all duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
